/**
 * G-Brain intelligence dashboard — project context, patterns, skills, and insights.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Brain, Play, Loader2, Clock, Sparkles } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { SelectProjectPrompt } from '@/components/projects/SelectProjectPrompt';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useProject } from '@/contexts/ProjectContext';
import { getGBrainContext, getGBrainPatterns, getGBrainSkills, analyzeGBrain } from '@/lib/api';
import { CATEGORY_COLORS } from '@/lib/pattern-categories';
import type { CodebaseResponse } from '@/lib/api';

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function GBrainPage(): React.ReactElement {
  const navigate = useNavigate();
  const { selectedProjectId, setSelectedProjectId, codebases, isLoadingCodebases } = useProject();
  const codebase = codebases?.find(cb => cb.id === selectedProjectId);
  const [activeTab, setActiveTab] = useState<'overview' | 'patterns' | 'skills'>('overview');

  const cid = selectedProjectId ?? '';

  const { data: contextData } = useQuery({
    queryKey: ['g-brain-context', cid],
    queryFn: () => getGBrainContext(cid),
    enabled: !!cid,
  });

  const { data: patternsData } = useQuery({
    queryKey: ['g-brain-patterns', cid],
    queryFn: () => getGBrainPatterns(cid),
    enabled: !!cid,
  });

  const { data: skillsData } = useQuery({
    queryKey: ['g-brain-skills', cid],
    queryFn: () => getGBrainSkills(cid),
    enabled: !!cid,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => analyzeGBrain(cid),
    onSuccess: result => {
      void navigate(`/chat/${result.conversationId}`);
    },
  });

  const context = contextData?.context;
  const patterns = patternsData?.patterns ?? [];
  const skills = skillsData?.skills ?? [];

  // Pattern category counts
  const categoryCounts = patterns.reduce<Record<string, number>>((acc, p) => {
    acc[p.category] = (acc[p.category] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="G-Brain" projectName={codebase?.name} />
      <PageBody contentClassName="space-y-6">
        {/* Project selector */}
        <div className="flex items-center gap-3">
          <label
            htmlFor="gbrain-project-select"
            className="text-sm text-text-secondary whitespace-nowrap"
          >
            Project:
          </label>
          <select
            id="gbrain-project-select"
            value={selectedProjectId ?? ''}
            onChange={(e): void => {
              setSelectedProjectId(e.target.value || null);
            }}
            className="h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring flex-1 max-w-md"
          >
            <option value="">Select a project...</option>
            {isLoadingCodebases ? (
              <option disabled>Loading...</option>
            ) : (
              codebases?.map((cb: CodebaseResponse) => (
                <option key={cb.id} value={cb.id}>
                  {cb.name}
                </option>
              ))
            )}
          </select>

          {selectedProjectId && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                analyzeMutation.mutate();
              }}
              disabled={analyzeMutation.isPending}
            >
              {analyzeMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-1 h-4 w-4" />
              )}
              Run Analysis
            </Button>
          )}
        </div>

        {!selectedProjectId ? (
          <SelectProjectPrompt
            prefix="Choose a project to"
            action="view its G-Brain intelligence"
          />
        ) : (
          <>
            {/* Tab switcher */}
            <div className="flex gap-1 border-b border-border">
              {(['overview', 'patterns', 'skills'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                  }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Project Context */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Brain className="h-4 w-4" />
                      Project Context
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {context ? (
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="text-text-secondary">Project Code:</span>{' '}
                          <span className="font-mono text-text-primary">{context.projectCode}</span>
                        </div>
                        {context.techStack.length > 0 && (
                          <div>
                            <span className="text-text-secondary">Tech Stack:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {context.techStack.map((t, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">
                                  {t.language}
                                  {t.framework ? `/${t.framework}` : ''}
                                  {t.testRunner ? ` (${t.testRunner})` : ''}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {Object.keys(context.conventions).length > 0 && (
                          <div>
                            <span className="text-text-secondary">Conventions:</span>
                            <div className="mt-1 space-y-1">
                              {Object.entries(context.conventions).map(([k, v]) => (
                                <div key={k} className="text-xs">
                                  <span className="font-mono text-text-tertiary">{k}:</span>{' '}
                                  <span className="text-text-primary">{v}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-text-tertiary">
                        No project context yet. Run an analysis or discovery workflow to initialize.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Pattern Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Sparkles className="h-4 w-4" />
                      Pattern Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {patterns.length > 0 ? (
                      <div className="space-y-3">
                        <div className="text-2xl font-bold text-text-primary">
                          {patterns.length}{' '}
                          <span className="text-sm font-normal text-text-secondary">
                            patterns recorded
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(categoryCounts).map(([cat, count]) => (
                            <Badge
                              key={cat}
                              className={CATEGORY_COLORS[cat] ?? 'bg-surface-elevated'}
                            >
                              {cat.replace(/_/g, ' ')}: {count}
                            </Badge>
                          ))}
                        </div>
                        {patterns.length > 0 && (
                          <div className="text-xs text-text-tertiary">
                            Latest:{' '}
                            {formatTimestamp(patterns[patterns.length - 1]?.timestamp ?? '')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-text-tertiary">
                        No patterns yet. They accumulate as you run discovery, test generation, and
                        convergence workflows.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'patterns' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Pattern Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  {patterns.length === 0 ? (
                    <div className="text-sm text-text-tertiary">No patterns recorded yet.</div>
                  ) : (
                    <div className="relative border-l border-border pl-4 space-y-3 max-h-[500px] overflow-auto">
                      {[...patterns].reverse().map((p, i) => (
                        <div key={i} className="relative">
                          <div className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3 text-text-tertiary" />
                            <span className="text-xs text-text-tertiary">
                              {formatTimestamp(p.timestamp)}
                            </span>
                            <Badge
                              className={`text-[10px] ${CATEGORY_COLORS[p.category] ?? 'bg-surface-elevated'}`}
                            >
                              {p.category.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          {Object.keys(p.data).length > 0 && (
                            <pre className="mt-1 text-[10px] text-text-secondary font-mono">
                              {JSON.stringify(p.data, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'skills' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">G-Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  {skills.length === 0 ? (
                    <div className="text-sm text-text-tertiary">
                      No custom G-skills yet. Skills are reusable methodology prompts that get
                      injected into conversations and workflows.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {skills.map(name => (
                        <div
                          key={name}
                          className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                        >
                          <Sparkles className="h-3.5 w-3.5 text-primary" />
                          <span className="text-sm text-text-primary">{name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </PageBody>
    </div>
  );
}
